import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { AlertTriangle, FileWarning, IdCard, ShieldAlert, ShieldCheck, ArrowRight, TrendingUp, TrendingDown, Minus, Trophy } from "lucide-react";

// Farbschema je Score-Bereich (Kontrast-geprüft, WCAG AA).
function scoreTheme(score) {
    if (score >= 90) return { ring: "#059669", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-300" };
    if (score >= 75) return { ring: "#B45309", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300" };
    if (score >= 50) return { ring: "#C2410C", text: "text-orange-700", bg: "bg-orange-50", border: "border-orange-300" };
    return { ring: "#B91C1C", text: "text-red-700", bg: "bg-red-50", border: "border-red-300" };
}

// Kompakter Score-Ring (SVG).
function ScoreRing({ score, color }) {
    const r = 26, c = 2 * Math.PI * r, off = c * (1 - Math.max(0, Math.min(100, score)) / 100);
    return (
        <svg width="72" height="72" viewBox="0 0 72 72" role="img" aria-label={`Compliance-Score ${score} von 100`} className="shrink-0">
            <circle cx="36" cy="36" r={r} fill="none" stroke="#E5E7EB" strokeWidth="7" />
            <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
                strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 36 36)"
                style={{ transition: "stroke-dashoffset 600ms ease" }} />
            <text x="36" y="40" textAnchor="middle" fontSize="18" fontWeight="800" fill="#111827">{score}</text>
        </svg>
    );
}

// Mini-Sparkline des Score-Verlaufs (letzte Snapshots).
function Sparkline({ history, color }) {
    const pts = (history || []).map((h) => h.score);
    if (pts.length < 2) return null;
    const w = 96, h = 28, max = 100, min = Math.min(40, ...pts);
    const span = Math.max(1, max - min);
    const step = w / (pts.length - 1);
    const path = pts.map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - ((v - min) / span) * h).toFixed(1)}`).join(" ");
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden="true">
            <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Dealer-Widget: Compliance-Score (0–100) + Trend + prüfungskritische Lücken.
export default function TaxComplianceGapsWidget({ className = "" }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data: d } = await api.get("/tax/compliance-score");
                if (alive) setData(d);
            } catch { /* optional */ }
        })();
        return () => { alive = false; };
    }, []);

    if (!data) return null;
    // Nur anzeigen, wenn tatsächlich steuerfreie innergemeinschaftliche Lieferungen/
    // Export-Geschäfte vorliegen (has_ig_business), Umsätze existieren UND der Händler
    // das Widget nicht deaktiviert hat. Ohne solche Geschäfte ist der Score nicht
    // einschlägig (der GoBD-Hinweis ist an anderer Stelle abgebildet).
    if (!data.has_turnover || !data.has_ig_business || data.widget_enabled === false) return null;
    const score = data.score ?? 100;
    const t = scoreTheme(score);
    const g = data.gaps || {};
    const delta = data.delta;
    const TrendIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
    const trendColor = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-600" : "text-gray-400";

    return (
        <div className={`${t.bg} border ${t.border} rounded-sm p-4 ${className}`} data-testid="tax-compliance-gaps-widget">
            {/* Score-Kopf */}
            <div className="flex items-center gap-4" data-testid="compliance-score-header">
                <ScoreRing score={score} color={t.ring} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        {score >= 90 ? <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" /> : <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />}
                        <h3 className="font-display font-black text-[#111827]">Compliance-Score</h3>
                        <span className={`text-xs font-bold ${t.text}`} data-testid="compliance-rating">{data.rating_label}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-600">
                        {(delta !== null && delta !== undefined) && (
                            <span className={`inline-flex items-center gap-0.5 font-bold ${trendColor}`} data-testid="compliance-trend">
                                <TrendIcon className="w-3.5 h-3.5" aria-hidden="true" />
                                {delta > 0 ? `+${delta}` : delta} ggü. Vormonat
                            </span>
                        )}
                        <Sparkline history={data.history} color={t.ring} />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">Steuer-/Prüfungs-Compliance zum heutigen Stichtag (100 = keine offenen Lücken).</p>
                    {data.benchmark?.available && (
                        <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#111827] bg-white/70 border border-gray-200 rounded-full px-2.5 py-1" data-testid="compliance-benchmark">
                            <Trophy className="w-3.5 h-3.5 text-[#B45309]" aria-hidden="true" />
                            <span>Besser als {data.benchmark.better_than_pct}% vergleichbarer Händler</span>
                            <span className="text-gray-400 font-normal">· Median {data.benchmark.median} (n={data.benchmark.cohort_size})</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Lücken-Karten */}
            {g.has_gaps && (
                <>
                    <div className="flex items-center gap-2 mt-4 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="text-sm font-bold text-[#111827]">Offene Steuernachweise</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                        {g.pending_gelangensnachweise > 0 && (
                            <Link to="/dealer/gelangensnachweis" className="group flex items-center gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2.5 hover:border-amber-400 transition-colors" data-testid="gap-pending-gn">
                                <FileWarning className="w-5 h-5 text-amber-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-[#111827]">{g.pending_gelangensnachweise} offene Gelangensnachweise</div>
                                    <div className="text-[11px] text-gray-500">{g.overdue_gelangensnachweise} davon überfällig (&gt; 7 Tage)</div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                        )}
                        {g.eu_customers_missing_ust_id > 0 && (
                            <Link to="/dealer/buchhaltung" className="group flex items-center gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2.5 hover:border-amber-400 transition-colors" data-testid="gap-missing-ust">
                                <IdCard className="w-5 h-5 text-amber-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-[#111827]">{g.eu_customers_missing_ust_id} EU-Kunden ohne USt-IdNr.</div>
                                    <div className="text-[11px] text-gray-500 truncate">
                                        {(g.eu_customers_missing_ust_id_samples || []).map((s) => `${s.name} (${s.country})`).join(", ") || "USt-IdNr. ergänzen"}
                                    </div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                        )}
                        {g.gobd_missing_finalizations > 0 && (
                            <Link to="/dealer/buchhaltung" className="group flex items-center gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2.5 hover:border-amber-400 transition-colors" data-testid="gap-gobd-missing">
                                <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-[#111827]">{g.gobd_missing_finalizations} Rechnungen nicht festgeschrieben</div>
                                    <div className="text-[11px] text-gray-500 truncate">Entwürfe &gt; 10 Tage offen – GoBD verlangt zeitnahe Festschreibung</div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                        )}
                        {g.reverse_charge_missing_ust_id > 0 && (
                            <Link to="/dealer/buchhaltung" className="group flex items-center gap-3 bg-white border border-amber-200 rounded-sm px-3 py-2.5 hover:border-amber-400 transition-colors" data-testid="gap-reverse-charge-ust">
                                <IdCard className="w-5 h-5 text-amber-600 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-[#111827]">{g.reverse_charge_missing_ust_id} Reverse-Charge-Rechnungen ohne USt-IdNr.</div>
                                    <div className="text-[11px] text-gray-500 truncate">
                                        {(g.reverse_charge_missing_ust_id_samples || []).map((s) => s.invoice_number).join(", ") || "USt-IdNr. des Empfängers ist Pflicht (§13b/§6a UStG)"}
                                    </div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                        )}
                    </div>
                    <p className="text-[11px] text-amber-700 mt-2.5">Diese Lücken können die Steuerfreiheit innergemeinschaftlicher Lieferungen gefährden — bitte vor der nächsten Betriebsprüfung schließen.</p>
                </>
            )}
        </div>
    );
}
